import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Lock, Pencil, Plus, Power, Search, Trash2, Users } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, type Partner, type PersonalOfferRule, type PersonalOfferRuleKey, type PersonalOffersSettings, type PromoAudience, type PromoCode, type PromoState } from "@/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AudienceBadge, blankPartner, blankPromoDraft, discountLabel, draftFromPromo, num, PARTNER_KIND_LABEL, PartnerEditorDialog, PromoEditorDialog, rewardLabel, rub, STATE_LABEL, StateBadge, dateRu, dateTimeRu } from "@/components/promo-kit";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { UgcArticlesTab } from "./ugc-articles";
type Tab = "codes" | "personal" | "partners" | "ugc" | "settings";

/** Право менять коды и партнёров; без него раздел открыт только на чтение. */
export const useCanManagePromos = () => {
  const $ = _c(2);
  const t0 = useAuth(_temp);
  let t1;
  if ($[0] !== t0) {
    t1 = hasAdminPermission(t0, "action.promos.manage");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Раздел открывается по праву page.promos. Без него — понятный отказ на месте:
 * раньше человек без права молча улетал в «Товары» и не понимал почему.
 */
export function PromosAccess(t0) {
  const $ = _c(1);
  const {
    children
  } = t0;
  const user = useAuth(_temp2);
  if (hasAdminPermission(user, "page.promos")) {
    return children;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-3 rounded-lg border p-8 text-center" data-testid="promos-denied"><Lock className="size-6 text-muted-foreground" /><h1 className="text-lg font-semibold">Нет доступа к разделу «Промокоды и партнёры»</h1><p className="text-sm text-muted-foreground">Нужно право «Раздел «Промокоды и партнёры»». Попросите администратора площадки добавить его вашей роли в разделе «Роли площадки».</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}

/** Код уже применяли: удалять нельзя — только в архив, статистика сохранится. */
function _temp2(s) {
  return s.user;
}
const usedPromo = (p: PromoCode): boolean => p.usedCount > 0 || (p.summary?.orders ?? 0) > 0 || (p.summary?.sellers ?? 0) > 0 || (p.summary?.sellersApproved ?? 0) > 0;
const TABS: {
  key: Tab;
  label: string;
}[] = [{
  key: "codes",
  label: "Промокоды"
}, {
  key: "personal",
  label: "Персональные"
}, {
  key: "partners",
  label: "Партнёры"
}, {
  key: "ugc",
  label: "UGC-Артикулы"
}, {
  key: "settings",
  label: "Настройки"
}];

/**
 * «Промокоды и партнёры». Промокод площадки получил владельца-партнёра и две
 * аудитории (покупательский — в оформлении заказа, продавцовый — при
 * регистрации магазина). Список с ключевыми цифрами, карточка кода со
 * статистикой, партнёры со сводной статистикой, окно привязки кода продавцом.
 */
export function PromosPage() {
  const $ = _c(28);
  const [params, setParams] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = params.get("tab") as Tab || "codes";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tab = t0;
  let t1;
  if ($[2] !== params || $[3] !== setParams) {
    t1 = next => {
      const p = new URLSearchParams(params);
      if (next === "codes") {
        p.delete("tab");
      } else {
        p.set("tab", next);
      }
      setParams(p, {
        replace: true
      });
    };
    $[2] = params;
    $[3] = setParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setTab = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div><h1 className="text-xl font-semibold">Промокоды и партнёры</h1><p className="text-sm text-muted-foreground">Коды площадки и партнёров: покупательские — в оформлении заказа, продавцовые — при регистрации магазина.</p></div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setTab || $[7] !== tab) {
    t3 = TABS.map(t => <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`promos-tab-${t.key}`} ref={tab === t.key ? _temp3 : undefined} onClick={() => setTab(t.key)} className={cn("-mb-px shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm", tab === t.key ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t.label}</button>);
    $[6] = setTab;
    $[7] = tab;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="flex gap-1 overflow-x-auto border-b" role="tablist">{t3}</div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== tab) {
    t5 = tab === "codes" && <CodesTab />;
    $[11] = tab;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== tab) {
    t6 = tab === "personal" && <PersonalTab />;
    $[13] = tab;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== tab) {
    t7 = tab === "partners" && <PartnersTab />;
    $[15] = tab;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== tab) {
    t8 = tab === "ugc" && <UgcArticlesTab />;
    $[17] = tab;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== tab) {
    t9 = tab === "settings" && <SettingsTab />;
    $[19] = tab;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className="flex flex-col gap-6">{t2}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}

// ── Промокоды ───────────────────────────────────────────────────────────────
function _temp3(el) {
  return el?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });
}
function CodesTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {
    data,
    error: loadError,
    isLoading
  } = useQuery({
    queryKey: ["promos"],
    queryFn: () => api.promos.list()
  });
  const partnersQ = useQuery({
    queryKey: ["partners", "list", ""],
    queryFn: () => api.partners.list()
  });
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState<"" | PromoAudience>("");
  const [state, setState] = useState<"" | PromoState>("");
  const [partnerId, setPartnerId] = useState(params.get("partner") ?? "");
  const canManage = useCanManagePromos();
  const [showArchive, setShowArchive] = useState(false);
  const [editor, setEditor] = useState<null | {
    code?: string;
    promo?: PromoCode;
  }>(null);
  const [removing, setRemoving] = useState<PromoCode | null>(null);
  const [archiving, setArchiving] = useState<PromoCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({
    queryKey: ["promos"]
  });
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter(p => (showArchive || state === "archived" || p.state !== "archived") && (!audience || p.audience === audience) && (!state || p.state === state) && (!partnerId || (partnerId === "none" ? !p.partnerId : p.partnerId === partnerId)) && (!q || `${p.code} ${p.description} ${p.partner?.name ?? ""}`.toLowerCase().includes(q)));
  }, [data, search, audience, state, partnerId, showArchive]);
  const archivedCount = (data ?? []).filter(p_0 => p_0.state === "archived").length;
  const toggle = async (p_1: PromoCode) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.promos.update(p_1.code, {
        active: !p_1.active
      });
      await refresh();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const setArchived = async (p_2: PromoCode, archived: boolean) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.promos.update(p_2.code, {
        archived
      });
      setArchiving(null);
      await refresh();
    } catch (e_0) {
      setActionError((e_0 as Error).message);
      setArchiving(null);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.promos.remove(removing.code);
      setRemoving(null);
      await refresh();
    } catch (e_1) {
      setActionError((e_1 as Error).message);
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  };
  const error = actionError ?? (loadError ? (loadError as Error).message : null);
  const selectClass = "h-9 rounded-md border border-input bg-background px-3 text-sm";
  return <div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-[220px] flex-1">
					<Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
					<Input className="pl-8" placeholder="Код, описание или партнёр" value={search} onChange={e_2 => setSearch(e_2.target.value)} data-testid="promos-search" />
				</div>
				<select className={selectClass} value={audience} onChange={e_3 => setAudience(e_3.target.value as "" | PromoAudience)} aria-label="Тип" data-testid="promos-filter-audience">
					<option value="">Все типы</option>
					<option value="buyer">Покупательские</option>
					<option value="seller">Продавцовые</option>
				</select>
				<select className={selectClass} value={state} onChange={e_4 => setState(e_4.target.value as "" | PromoState)} aria-label="Статус">
					<option value="">Все статусы</option>
					{(Object.keys(STATE_LABEL) as PromoState[]).map(s => <option key={s} value={s}>
							{STATE_LABEL[s]}
						</option>)}
				</select>
				<select className={selectClass} value={partnerId} onChange={e_5 => setPartnerId(e_5.target.value)} aria-label="Партнёр">
					<option value="">Все владельцы</option>
					<option value="none">Без партнёра</option>
					{(partnersQ.data ?? []).map(p_3 => <option key={p_3.id} value={p_3.id}>
							{p_3.name}
						</option>)}
				</select>
				{archivedCount > 0 && <label className="flex items-center gap-2 text-sm text-muted-foreground">
						<input type="checkbox" checked={showArchive} onChange={e_6 => setShowArchive(e_6.target.checked)} data-testid="promos-show-archive" />
						Архив ({num(archivedCount)})
					</label>}
				{canManage && <Button onClick={() => setEditor({})} data-testid="promo-create">
						<Plus className="mr-2 size-4" />
						Новый промокод
					</Button>}
			</div>

			{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

			<div className="overflow-x-auto rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Код</TableHead>
							<TableHead>Тип</TableHead>
							<TableHead>Владелец</TableHead>
							<TableHead>Скидка</TableHead>
							<TableHead>Статус</TableHead>
							<TableHead className="text-right">Использование</TableHead>
							<TableHead className="text-right">Оборот</TableHead>
							<TableHead className="text-right">Скидки</TableHead>
							<TableHead className="text-right">Партнёру</TableHead>
							<TableHead className="text-right">Действия</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading && <TableRow>
								<TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">
									Загружаем…
								</TableCell>
							</TableRow>}
						{!isLoading && items.length === 0 && <TableRow>
								<TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">
									{(data ?? []).length === 0 ? "Промокодов пока нет" : "Под фильтр ничего не подошло"}
								</TableCell>
							</TableRow>}
						{items.map(p_4 => {
            const s_0 = p_4.summary;
            const seller = p_4.audience === "seller";
            return <TableRow key={p_4.code} className="cursor-pointer" data-testid={`promo-row-${p_4.code}`} onClick={() => navigate(`/promos/${encodeURIComponent(p_4.code)}`)}>
									<TableCell className="font-mono font-medium">
										{p_4.code}
										{p_4.description && <div className="font-sans text-xs font-normal text-muted-foreground">{p_4.description}</div>}
									</TableCell>
									<TableCell>
										<AudienceBadge audience={p_4.audience} />
									</TableCell>
									<TableCell onClick={e_7 => e_7.stopPropagation()}>
										{p_4.partner ? <Link className="hover:underline" to={`/promos/partners/${p_4.partner.id}`}>
												{p_4.partner.name}
											</Link> : p_4.shopId ? <span title="Промокод магазина: скидка за счёт магазина">Магазин «{p_4.shopName ?? p_4.shopId}»</span> : <span className="text-muted-foreground">Площадка</span>}
									</TableCell>
									<TableCell className="whitespace-nowrap text-sm">
										{discountLabel(p_4)}
										{p_4.firstOrderOnly && <div className="text-xs text-muted-foreground">новым на OSMIX</div>}
										{p_4.newToShopOnly && <div className="text-xs text-muted-foreground">не покупал в магазине</div>}
									</TableCell>
									<TableCell>
										<StateBadge state={p_4.state} />
										<div className="mt-0.5 whitespace-nowrap text-xs text-muted-foreground">
											{!p_4.startsAt && !p_4.expiresAt ? "бессрочно" : `${p_4.startsAt ? dateRu(p_4.startsAt) : "…"} — ${p_4.expiresAt ? dateRu(p_4.expiresAt) : "…"}`}
										</div>
									</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">
										{seller ? <>
												{num(s_0?.sellers ?? 0)} прод.
												<div className="text-xs text-muted-foreground">модерация: {num(s_0?.sellersApproved ?? 0)}</div>
											</> : <>
												{num(s_0?.orders ?? 0)} зак.
												<div className="text-xs text-muted-foreground">
													{num(s_0?.buyers ?? 0)} покуп.{p_4.maxUses != null ? ` · лимит ${num(p_4.maxUses)}` : ""}
												</div>
											</>}
									</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">
										{rub(seller ? s_0?.sellerGmv ?? 0 : (s_0?.gmv ?? 0) - (s_0?.discount ?? 0))}
									</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">{seller ? "—" : rub(s_0?.discount ?? 0)}</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums" title={rewardLabel(p_4)}>
										{p_4.rewardKind === "none" ? "—" : rub(s_0?.reward ?? 0)}
									</TableCell>
									<TableCell className="text-right" onClick={e_8 => e_8.stopPropagation()}>
										<div className="flex justify-end gap-1">
											{canManage && p_4.state !== "archived" && <>
													<Button variant="ghost" size="icon" title={p_4.active ? "Выключить" : "Включить"} disabled={busy} onClick={() => void toggle(p_4)}>
														<Power className="size-4" />
													</Button>
													<Button variant="ghost" size="icon" title="Изменить" disabled={busy} onClick={() => setEditor({
                      code: p_4.code,
                      promo: p_4
                    })}>
														<Pencil className="size-4" />
													</Button>
												</>}
											{canManage && p_4.state === "archived" && <Button variant="ghost" size="icon" title="Вернуть из архива" disabled={busy} onClick={() => void setArchived(p_4, false)} data-testid={`promo-unarchive-${p_4.code}`}>
													<ArchiveRestore className="size-4" />
												</Button>}
											{canManage && p_4.state !== "archived" && usedPromo(p_4) && <Button variant="ghost" size="icon" title="В архив" disabled={busy} onClick={() => setArchiving(p_4)} data-testid={`promo-archive-${p_4.code}`}>
													<Archive className="size-4" />
												</Button>}
											{canManage && !usedPromo(p_4) && <Button variant="ghost" size="icon" title="Удалить" disabled={busy} onClick={() => setRemoving(p_4)}>
													<Trash2 className="size-4 text-destructive" />
												</Button>}
										</div>
									</TableCell>
								</TableRow>;
          })}
					</TableBody>
				</Table>
			</div>

			{editor && <PromoEditorDialog key={editor.code ?? "new"} open editingCode={editor.code} initial={editor.promo ? draftFromPromo(editor.promo) : blankPromoDraft("buyer", partnerId && partnerId !== "none" ? partnerId : "")} onClose={() => setEditor(null)} onSaved={async saved => {
      setEditor(null);
      await refresh();
      if (!editor.code) navigate(`/promos/${encodeURIComponent(saved.code)}`);
    }} />}
			<ConfirmDialog open={archiving !== null} title={`Отправить ${archiving?.code ?? ""} в архив?`} description={archiving ? archiving.audience === "seller" ? `По коду зарегистрировались продавцов: ${num(archiving.summary?.sellers ?? 0)}, прошли модерацию: ${num(archiving.summary?.sellersApproved ?? 0)}. Код перестанет приниматься, статистика и привязки магазинов сохранятся.` : `Код применили в заказах: ${num(archiving.summary?.orders ?? 0)} (покупателей: ${num(archiving.summary?.buyers ?? 0)}). Код перестанет применяться, статистика сохранится. Вернуть из архива можно в любой момент.` : undefined} confirmLabel="В архив" busy={busy} onConfirm={() => archiving && void setArchived(archiving, true)} onClose={() => setArchiving(null)} />
			<ConfirmDialog open={removing !== null} title={`Удалить промокод ${removing?.code ?? ""}?`} description="Код ни разу не применялся — его можно удалить без потери истории." confirmLabel="Удалить" confirmVariant="destructive" busy={busy} onConfirm={() => void remove()} onClose={() => setRemoving(null)} />
		</div>;
}

// ── Партнёры ────────────────────────────────────────────────────────────────

function PartnersTab() {
  const $ = _c(50);
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  let t0;
  if ($[0] !== search) {
    t0 = search.trim();
    $[0] = search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = ["partners", "list", t0];
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== search) {
    t2 = () => api.partners.list(search.trim());
    $[4] = search;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data,
    error,
    isLoading
  } = useQuery(t3);
  const [creating, setCreating] = useState(false);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => setSearch(e.target.value);
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== search) {
    t6 = <div className="relative min-w-[220px] flex-1">{t4}<Input className="pl-8" placeholder={"\u0418\u043C\u044F, \u043F\u043E\u0447\u0442\u0430, \u0442\u0435\u043B\u0435\u0444\u043E\u043D, Telegram \u0438\u043B\u0438 \u043A\u043E\u0434"} value={search} onChange={t5} data-testid="partners-search" /></div>;
    $[11] = search;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setCreating(true);
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = !canManage;
  const t9 = canManage ? undefined : "\u041D\u0443\u0436\u043D\u043E \u043F\u0440\u0430\u0432\u043E \xAB\u0423\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u0430\u043C\u0438 \u0438 \u043F\u0430\u0440\u0442\u043D\u0451\u0440\u0430\u043C\u0438\xBB";
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Plus className="mr-2 size-4" />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t8 || $[16] !== t9) {
    t11 = <Button onClick={t7} data-testid="partner-create" disabled={t8} title={t9}>{t10}Новый партнёр</Button>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t6) {
    t12 = <div className="flex flex-wrap items-center gap-2">{t6}{t11}</div>;
    $[18] = t11;
    $[19] = t6;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== error) {
    t13 = error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{(error as Error).message}</p>;
    $[21] = error;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <TableHeader><TableRow><TableHead>Партнёр</TableHead><TableHead>Контакты</TableHead><TableHead>Пользователь площадки</TableHead><TableHead className="text-right">Промокодов</TableHead><TableHead>Статус</TableHead><TableHead>Добавлен</TableHead></TableRow></TableHeader>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== isLoading) {
    t15 = isLoading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Загружаем…</TableCell></TableRow>;
    $[24] = isLoading;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== data || $[27] !== isLoading || $[28] !== search) {
    t16 = !isLoading && (data ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground"><Users className="mx-auto mb-2 size-6" />{search.trim() ? "\u041D\u0438\u043A\u043E\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u0438" : "\u041F\u0430\u0440\u0442\u043D\u0451\u0440\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442. \u0417\u0430\u0432\u0435\u0434\u0438\u0442\u0435 \u043F\u0435\u0440\u0432\u043E\u0433\u043E \u0438 \u043F\u0440\u0438\u0432\u044F\u0436\u0438\u0442\u0435 \u043A \u043D\u0435\u043C\u0443 \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u044B."}</TableCell></TableRow>;
    $[26] = data;
    $[27] = isLoading;
    $[28] = search;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== data) {
    t17 = data ?? [];
    $[30] = data;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== navigate || $[33] !== t17) {
    let t19;
    if ($[35] !== navigate) {
      t19 = p => <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/promos/partners/${p.id}`)} data-testid={`partner-row-${p.id}`}><TableCell><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{PARTNER_KIND_LABEL[p.kind]}</div></TableCell><TableCell className="text-sm">{[p.contactName, p.phone, p.email, p.telegram].filter(Boolean).join(" \xB7 ") || <span className="text-muted-foreground">—</span>}</TableCell><TableCell className="text-sm" onClick={_temp4}>{p.user ? <Link className="hover:underline" to={`/users/${p.user.id}`}>{p.user.shopName || p.user.name || p.user.email}</Link> : <span className="text-muted-foreground">—</span>}</TableCell><TableCell className="text-right tabular-nums">{num(p.promoCount)}</TableCell><TableCell><span className={cn("rounded-full px-2 py-0.5 text-xs", p.active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground")}>{p.active ? "\u0410\u043A\u0442\u0438\u0432\u0435\u043D" : "\u0412\u044B\u043A\u043B\u044E\u0447\u0435\u043D"}</span></TableCell><TableCell className="text-sm text-muted-foreground">{dateRu(p.createdAt)}</TableCell></TableRow>;
      $[35] = navigate;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    t18 = t17.map(t19);
    $[32] = navigate;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t18) {
    t19 = <div className="overflow-x-auto rounded-lg border"><Table>{t14}<TableBody>{t15}{t16}{t18}</TableBody></Table></div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== creating || $[42] !== navigate || $[43] !== queryClient) {
    t20 = creating && <PartnerEditorDialog open={true} initial={blankPartner()} onClose={() => setCreating(false)} onSaved={async saved => {
      setCreating(false);
      await queryClient.invalidateQueries({
        queryKey: ["partners"]
      });
      navigate(`/promos/partners/${saved.id}`);
    }} />;
    $[41] = creating;
    $[42] = navigate;
    $[43] = queryClient;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t12 || $[46] !== t13 || $[47] !== t19 || $[48] !== t20) {
    t21 = <div className="flex flex-col gap-4">{t12}{t13}{t19}{t20}</div>;
    $[45] = t12;
    $[46] = t13;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}

// ── Настройки ───────────────────────────────────────────────────────────────
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function SettingsTab() {
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  const {
    data
  } = useQuery({
    queryKey: ["promo-settings"],
    queryFn: () => api.promos.settings()
  });
  const [days, setDays] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const value = days ?? String(data?.sellerBindDays ?? "");
  const save = async () => {
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n) || n < 0) {
      setMessage("Укажите число дней от 0");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api.promos.updateSettings({
        sellerBindDays: n
      });
      await queryClient.invalidateQueries({
        queryKey: ["promo-settings"]
      });
      setDays(null);
      setMessage("Сохранено");
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex max-w-xl flex-col gap-4 rounded-lg border p-5">
			<div>
				<h2 className="font-medium">Промокод партнёра у продавца</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					Продавец указывает код партнёра при регистрации, в анкете или в профиле. Пока магазин не прошёл модерацию — всегда; после —
					в течение этого числа дней с регистрации. Позже изменить привязку может только администратор в карточке магазина.
				</p>
			</div>
			<label className="flex flex-col gap-1 text-sm">
				<span className="text-muted-foreground">Дней после регистрации</span>
				<Input type="number" min={0} className="w-40" value={value} onChange={e_0 => setDays(e_0.target.value)} data-testid="promo-bind-days" />
			</label>
			<div className="flex items-center gap-3">
				<Button onClick={() => void save()} disabled={!canManage || busy || days === null} data-testid="promo-settings-save">
					Сохранить
				</Button>
				{message && <span className="text-sm text-muted-foreground">{message}</span>}
			</div>
		</div>;
}

// ── Персональные предложения (fd-promo-12) ─────────────────────────────────

const RULE_TITLE: Record<PersonalOfferRuleKey, {
  title: string;
  threshold: string | null;
}> = {
  abandoned_cart: {
    title: "Брошенная корзина",
    threshold: "Корзина без движения, дней"
  },
  favorite_price_drop: {
    title: "Товар из избранного подешевел или вернулся в наличие",
    threshold: "Сигнал не старше, дней"
  },
  inactive_buyer: {
    title: "Нет заказов",
    threshold: "Без заказов, дней"
  }
};
const RULE_KEYS = Object.keys(RULE_TITLE) as PersonalOfferRuleKey[];
type RuleDraft = Record<keyof PersonalOfferRule, string | boolean>;
const ruleDraftOf = (r: PersonalOfferRule): RuleDraft => ({
  enabled: r.enabled,
  kind: r.kind,
  value: r.kind === "percent" ? String(Math.round(r.value * 1000) / 10) : String(r.value),
  minSubtotal: String(r.minSubtotal),
  validDays: String(r.validDays),
  periodDays: String(r.periodDays),
  thresholdDays: String(r.thresholdDays)
});
const ruleOf = (d: RuleDraft): PersonalOfferRule => {
  const n = (v: string | boolean) => Number(String(v).replace(",", "."));
  const kind = d.kind as PersonalOfferRule["kind"];
  const int = (v: string | boolean, min: number) => Math.max(min, Math.floor(n(v)) || min);
  return {
    enabled: Boolean(d.enabled),
    kind,
    value: kind === "free_delivery" ? 0 : kind === "percent" ? n(d.value) / 100 : n(d.value),
    minSubtotal: Math.max(0, n(d.minSubtotal) || 0),
    validDays: int(d.validDays, 1),
    periodDays: int(d.periodDays, 1),
    thresholdDays: int(d.thresholdDays, 1)
  };
};
const PERSONAL_RULE_LABEL: Record<string, string> = {
  abandoned_cart: "Брошенная корзина",
  favorite_price_drop: "Избранное",
  inactive_buyer: "Нет заказов"
};
function PersonalTab() {
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ["personal-offers"],
    queryFn: () => api.promos.personalOffers()
  });
  const [drafts, setDrafts] = useState<Record<PersonalOfferRuleKey, RuleDraft> | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const current = drafts ?? (data ? Object.fromEntries(RULE_KEYS.map(k => [k, ruleDraftOf(data.settings.rules[k])])) as Record<PersonalOfferRuleKey, RuleDraft> : null);
  const setField = (key: PersonalOfferRuleKey, field: keyof PersonalOfferRule, value: string | boolean) => {
    if (!current) return;
    setDrafts({
      ...current,
      [key]: {
        ...current[key],
        [field]: value
      }
    });
  };
  const save = async () => {
    if (!current) return;
    setBusy(true);
    setMessage(null);
    try {
      const rules = Object.fromEntries(RULE_KEYS.map(k_0 => [k_0, ruleOf(current[k_0])])) as PersonalOffersSettings["rules"];
      await api.promos.updatePersonalOffers({
        rules
      });
      await queryClient.invalidateQueries({
        queryKey: ["personal-offers"]
      });
      setDrafts(null);
      setMessage("Сохранено");
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const runNow = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.promos.runPersonalOffers();
      await queryClient.invalidateQueries({
        queryKey: ["personal-offers"]
      });
      setMessage(`Выдано кодов: ${num(result.created)}`);
    } catch (e_0) {
      setMessage((e_0 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (isLoading) return <p className="text-sm text-muted-foreground">Загрузка…</p>;
  if (error || !data || !current) return <p className="text-sm text-destructive">{(error as Error | null)?.message ?? "Не удалось загрузить"}</p>;
  return <div className="flex flex-col gap-6" data-testid="personal-offers">
			<div className="flex flex-col gap-1">
				<p className="text-sm text-muted-foreground">
					Персональный код получает конкретный покупатель: применить его может только он. Коды выдаются раз в сутки по правилам ниже —
					покупателю приходит уведомление, код появляется у него в «Мои купоны». Один код на покупателя по правилу в период.
				</p>
				{data.lastRun && <p className="text-xs text-muted-foreground">
						Последний запуск: {dateTimeRu(data.lastRun.ranAt)}, выдано {num(data.lastRun.created)}
					</p>}
			</div>
			<div className="grid gap-4 lg:grid-cols-3">
				{RULE_KEYS.map(key_0 => {
        const d = current[key_0];
        const kind = d.kind as PersonalOfferRule["kind"];
        return <section key={key_0} className="flex flex-col gap-3 rounded-lg border p-4" data-testid={`personal-rule-${key_0}`}>
							<label className="flex items-center gap-2 text-sm font-medium">
								<input type="checkbox" checked={Boolean(d.enabled)} onChange={e_1 => setField(key_0, "enabled", e_1.target.checked)} />
								{RULE_TITLE[key_0].title}
							</label>
							<div className="grid grid-cols-2 gap-3 text-sm">
								<label className="col-span-2 flex flex-col gap-1">
									<span className="text-muted-foreground">Предложение</span>
									<select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={kind} onChange={e_2 => setField(key_0, "kind", e_2.target.value)}>
										<option value="percent">Скидка, %</option>
										<option value="fixed">Скидка, руб.</option>
										<option value="free_delivery">Бесплатная доставка</option>
									</select>
								</label>
								{kind !== "free_delivery" && <label className="flex flex-col gap-1">
										<span className="text-muted-foreground">{kind === "percent" ? "Размер, %" : "Размер, руб."}</span>
										<Input type="number" min={0} value={String(d.value)} onChange={e_3 => setField(key_0, "value", e_3.target.value)} />
									</label>}
								<label className="flex flex-col gap-1">
									<span className="text-muted-foreground">Мин. сумма, руб.</span>
									<Input type="number" min={0} value={String(d.minSubtotal)} onChange={e_4 => setField(key_0, "minSubtotal", e_4.target.value)} />
								</label>
								{RULE_TITLE[key_0].threshold && <label className="flex flex-col gap-1">
										<span className="text-muted-foreground">{RULE_TITLE[key_0].threshold}</span>
										<Input type="number" min={1} value={String(d.thresholdDays)} onChange={e_5 => setField(key_0, "thresholdDays", e_5.target.value)} />
									</label>}
								<label className="flex flex-col gap-1">
									<span className="text-muted-foreground">Код действует, дней</span>
									<Input type="number" min={1} value={String(d.validDays)} onChange={e_6 => setField(key_0, "validDays", e_6.target.value)} />
								</label>
								<label className="flex flex-col gap-1">
									<span className="text-muted-foreground">Не чаще раза в, дней</span>
									<Input type="number" min={1} value={String(d.periodDays)} onChange={e_7 => setField(key_0, "periodDays", e_7.target.value)} />
								</label>
							</div>
						</section>;
      })}
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<Button onClick={() => void save()} disabled={!canManage || busy || drafts === null} data-testid="personal-save">
					Сохранить правила
				</Button>
				<Button variant="outline" onClick={() => void runNow()} disabled={!canManage || busy} data-testid="personal-run">
					Выдать коды сейчас
				</Button>
				{message && <span className="text-sm text-muted-foreground">{message}</span>}
			</div>
			<div className="overflow-x-auto rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Код</TableHead>
							<TableHead>Покупатель</TableHead>
							<TableHead>Правило</TableHead>
							<TableHead>Скидка</TableHead>
							<TableHead>Состояние</TableHead>
							<TableHead className="text-right">Выдан</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.codes.length === 0 && <TableRow>
								<TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
									Персональных кодов пока нет
								</TableCell>
							</TableRow>}
						{data.codes.map(p => <TableRow key={p.code} data-testid={`personal-row-${p.code}`}>
								<TableCell className="font-mono font-medium">{p.code}</TableCell>
								<TableCell>
									{p.userId ? <Link className="hover:underline" to={`/users/${encodeURIComponent(p.userId)}`}>
											{p.userName || p.userId}
										</Link> : "—"}
								</TableCell>
								<TableCell>{PERSONAL_RULE_LABEL[p.personalRule ?? ""] ?? "Вручную"}</TableCell>
								<TableCell className="whitespace-nowrap">{discountLabel(p)}</TableCell>
								<TableCell>
									<StateBadge state={p.state} />
									{p.expiresAt && <div className="mt-0.5 text-xs text-muted-foreground">до {dateRu(p.expiresAt)}</div>}
								</TableCell>
								<TableCell className="whitespace-nowrap text-right text-sm">{dateRu(p.createdAt)}</TableCell>
							</TableRow>)}
					</TableBody>
				</Table>
			</div>
		</div>;
}
function _temp(s) {
  return s.user;
}